import { Typography, Paper, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CheckDetailTitle from './CheckDetailTitle'
import TimelineDotLine from '../components/TimelineDotLine'
import CheckRiskStatus from '../components/CheckRiskStatus'
import DaysTimeline from '../components/DaysLine'
import SanctionsSummaryPdf from './SanctionsSummaryPdf'
import './checks.scss'

const CheckKyc = ({ kycCheck, level = 0 }) => {
  const { t } = useTranslation(['common', 'messages'])
  const details = kycCheck?.checkDetails || {}
  const yesColor = { color: '#ff8a00' }
  const noColor = { color: 'text.secondary' }

  const renderPepStatus = (isPep) => {
    return isPep ? (
      <Chip label={t('common:yes')} sx={yesColor} size="small" />
    ) : (
      <Chip label={t('common:no')} sx={noColor} size="small" />
    )
  }

  const getAnySanctionsOrPep = (details) => {
    if (!details) return { hasAny: false, total: 0 }

    const sanctionsFields = [
      'Last365DaysSanctions',
      'Last180DaysSanctions',
      'Last90DaysSanctions',
      'Last30DaysSanctions',
    ]

    const pepFields = [
      'Last5PlusYearsPEPOccurence',
      'Last5YearsPEPOccurence',
      'Last3YearsPEPOccurence',
      'LastYearPEPOccurence',
    ]

    const allFields = [...sanctionsFields, ...pepFields]

    let total = 0
    let hasAny = false

    allFields.forEach((field) => {
      const value = Number(details[field]) || 0
      total += value
      if (value > 0) hasAny = true
    })

    return { hasAny, total }
  }

  const { hasAny, total } = getAnySanctionsOrPep(details)

  return (
    <>
      <Paper elevation={0} variant="colorPrimary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography>{t('common:kyc')}</Typography>
        <CheckRiskStatus checkLength={total} hasChecks={hasAny} />
      </Paper>

      <CheckDetailTitle
        email={kycCheck?.email}
        status={kycCheck?.status}
        createdAt={kycCheck?.createdAt}
        updatedAt={kycCheck?.updatedAt}
      />

      <TimelineDotLine>
        <div className="c-detail-col" style={{ gap: '1rem' }}>
          <Paper variant="rootOutlined" elevation={0} sx={{ flex: 1 }}>
            <div className="c-detail-grid">
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:isCurrentlyPep')}</Typography>
                <Typography variant="subtitle3">{renderPepStatus(details.IsCurrentlyPEP)}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:isCurrentlySanctioned')}</Typography>
                <Typography variant="subtitle3">{renderPepStatus(details.IsCurrentlySanctioned)}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:wasPreviouslySanctioned')}</Typography>
                <Typography variant="subtitle3">{renderPepStatus(details.WasPreviouslySanctioned)}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:isCurrentlyElectoralDonor')}</Typography>
                <Typography variant="subtitle3">{renderPepStatus(details.IsCurrentlyElectoralDonor)}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:isHistoricalElectoralDonor')}</Typography>
                <Typography variant="subtitle3">{renderPepStatus(details.IsHistoricalElectoralDonor)}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">
                  {t('common:total')} {t('common:electoralDonations')} {t('common:lastTwoElections')}
                </Typography>
                <Typography variant="subtitle3">{details.TotalElectoralDonationsLastTwoElections ?? '---'}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">
                  {t('common:total')} {t('common:electoralDonations')}
                </Typography>
                <Typography variant="subtitle3">{details.TotalElectoralDonations ?? '---'}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">
                  {t('common:total')} {t('common:electoralDonations')} {t('common:amount')}{' '}
                  {t('common:lastTwoElections')}
                </Typography>
                <Typography variant="subtitle3">
                  {details.TotalElectoralDonationAmountLastTwoElections ?? '---'}
                </Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">
                  {t('common:total')} {t('common:electoralDonations')} {t('common:amount')}
                </Typography>
                <Typography variant="subtitle3">{details.TotalElectoralDonationAmount ?? '---'}</Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:firstSanctionDate')}</Typography>
                <Typography variant="subtitle3">
                  {details.FirstSanctionDate && !details.FirstSanctionDate?.includes('0001-01-01')
                    ? details.FirstSanctionDate
                    : '---'}
                </Typography>
              </div>
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:lastSanctionDate')}</Typography>
                <Typography variant="subtitle3">
                  {details.LastSanctionDate && !details.LastSanctionDate?.includes('0001-01-01')
                    ? details.LastSanctionDate
                    : '---'}
                </Typography>
              </div>
              {details.informação && (
                <div className="c-detail-row">
                  <Typography variant="subtitle1">{t('common:data')}</Typography>
                  <Typography variant="subtitle3">{details.informação}</Typography>
                </div>
              )}
            </div>
          </Paper>

          <Paper variant="rootOutlined" elevation={0} className="c-detail-col">
            <Typography variant="subtitle1">{t('common:sanctions')}</Typography>
            <DaysTimeline
              ranges={[
                { label: t('common:last365'), count: details.Last365DaysSanctions ?? 0 },
                { label: t('common:last180'), count: details.Last180DaysSanctions ?? 0 },
                { label: t('common:last90'), count: details.Last90DaysSanctions ?? 0 },
                { label: t('common:last30'), count: details.Last30DaysSanctions ?? 0 },
              ]}
            />
          </Paper>
          <Paper variant="rootOutlined" elevation={0} className="c-detail-col">
            <Typography variant="subtitle1">{t('common:pepOccurrences')}</Typography>
            <DaysTimeline
              ranges={[
                { label: t('common:last5PlusYearsPEPOccurence'), count: details.Last5PlusYearsPEPOccurence ?? 0 },
                { label: t('common:last5YearsPEPOccurence'), count: details.Last5YearsPEPOccurence ?? 0 },
                { label: t('common:last3YearsPEPOccurence'), count: details.Last3YearsPEPOccurence ?? 0 },
                { label: t('common:lastYearPEPOccurence'), count: details.LastYearPEPOccurence ?? 0 },
              ]}
            />
          </Paper>
        </div>
      </TimelineDotLine>

      <SanctionsSummaryPdf checkDetails={details} />
    </>
  )
}

export default CheckKyc

/* import { Box, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CheckDetailTitle from './CheckDetailTitle'
import SanctionsSummary from './KybSanctionsSummary/SanctionsSummary'

const variantByLevel = {
  0: 'h3',
  1: 'h4',
}

const CheckKyc = ({ title = '', kycCheck, level = 0 }) => {
  const { t } = useTranslation(['common', 'messages'])
  return (
    <Box className="detail-group">
      <Typography variant={variantByLevel[level] || variantByLevel[0]}>{title || t('common:kyc')}</Typography>
      <CheckDetailTitle
        email={kycCheck?.email}
        status={kycCheck?.status}
        createdAt={kycCheck?.createdAt}
        updatedAt={kycCheck?.updatedAt}
      />
      <SanctionsSummary checkDetails={kycCheck} />

      <Box className="">
        <h4>
          {t('common:kyc')} {t('common:details')}
        </h4>
        <Box className="detail-group-grid-4">
          <Box>
            <span>{t('common:isCurrentlyPep')}</span>
            <p>{kycCheck?.checkDetails?.IsCurrentlyPEP ? t('common:yes') : t('common:no')}</p>
          </Box>
          <Box>
            <span>{t('common:isCurrentlySanctioned')}</span>
            <p>{kycCheck?.checkDetails?.IsCurrentlySanctioned ? 'Yes' : 'No'}</p>
          </Box>
          <Box>
            <span>{t('common:wasPreviouslySanctioned')}</span>
            <p>{kycCheck?.checkDetails?.WasPreviouslySanctioned ? 'Yes' : 'No'}</p>
          </Box>
          <Box>
            <span>
              {t('common:last30')} {t('common:sanctions')}
            </span>
            <p>{kycCheck?.checkDetails?.Last30DaysSanctions ?? '---'}</p>
          </Box>
          <Box>
            <span>
              {t('common:last90')} {t('common:sanctions')}
            </span>
            <p>{kycCheck?.checkDetails?.Last90DaysSanctions ?? '---'}</p>
          </Box>
          <Box>
            <span>
              {t('common:last180')} {t('common:sanctions')}
            </span>
            <p>{kycCheck?.checkDetails?.Last180DaysSanctions ?? '---'}</p>
          </Box>
          <Box>
            <span>
              {t('common:last365')} {t('common:sanctions')}
            </span>
            <p>{kycCheck?.checkDetails?.Last365DaysSanctions ?? '---'}</p>
          </Box>
          <Box>
            <span>{t('common:lastYearPEPOccurence')}</span>
            <p>{kycCheck?.checkDetails?.LastYearPEPOccurence ?? '---'}</p>
          </Box>
          <Box>
            <span>{t('common:last3YearsPEPOccurence')}</span>
            <p>{kycCheck?.checkDetails?.Last3YearsPEPOccurence ?? '---'}</p>
          </Box>
          <Box>
            <span>{t('common:last5YearsPEPOccurence')}</span>
            <p>{kycCheck?.checkDetails?.Last5YearsPEPOccurence ?? '---'}</p>
          </Box>
          <Box>
            <span>{t('common:last5PlusYearsPEPOccurence')}</span>
            <p>{kycCheck?.checkDetails?.Last5PlusYearsPEPOccurence ?? '---'}</p>
          </Box>
          <Box>
            <span>{t('common:firstSanctionDate')}</span>
            <p>
              {!kycCheck?.checkDetails?.FirstSanctionDate?.includes('0001-01-01')
                ? kycCheck?.checkDetails?.FirstSanctionDate
                : '---'}
            </p>
          </Box>
          <Box>
            <span>{t('common:lastSanctionDate')}</span>
            <p>
              {!kycCheck?.checkDetails?.LastSanctionDate?.includes('0001-01-01')
                ? kycCheck?.checkDetails?.LastSanctionDate
                : '---'}
            </p>
          </Box>
          <Box>
            <span>{t('common:isCurrentlyElectoralDonor')}</span>
            <p>{kycCheck?.checkDetails?.IsCurrentlyElectoralDonor ? 'Yes' : 'No'}</p>
          </Box>
          <Box>
            <span>{t('common:isHistoricalElectoralDonor')}</span>
            <p>{kycCheck?.checkDetails?.IsHistoricalElectoralDonor ? 'Yes' : 'No'}</p>
          </Box>
          <Box>
            <span>
              {t('common:total')} {t('common:electoralDonations')} {t('common:lastTwoElections')}
            </span>
            <p>{kycCheck?.checkDetails?.TotalElectoralDonationsLastTwoElections ?? '---'}</p>
          </Box>
          <Box>
            <span>
              {t('common:total')} {t('common:electoralDonations')}
            </span>
            <p>{kycCheck?.checkDetails?.TotalElectoralDonations ?? '---'}</p>
          </Box>
          <Box>
            <span>
              {t('common:total')} {t('common:electoralDonations')} {t('common:amount')} {t('common:lastTwoElections')}
            </span>
            <p>{kycCheck?.checkDetails?.TotalElectoralDonationAmountLastTwoElections ?? '---'}</p>
          </Box>
          <Box>
            <span>
              {t('common:total')} {t('common:electoralDonations')} {t('common:amount')}
            </span>
            <p>{kycCheck?.checkDetails?.TotalElectoralDonationAmount ?? '---'}</p>
          </Box>
        </Box>
        {kycCheck?.checkDetails?.informação && (
          <Box>
            <span>{t('common:data')}</span>
            <p>{kycCheck?.checkDetails?.informação}</p>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CheckKyc
 */
